<template>
  <div v-if="isNotificationsEnabled">
    <div class="relative cursor-pointer" @click="open = true">
      <PulseLineBell class="w-4 h-4 text-typography-primary fill-current" />
      <div
        v-if="unreadCount > 0"
        class="absolute -top-3 -right-4 text-[11px] font-medium text-primary-700 bg-neutral-200 rounded min-w-[20px] min-h-[20px] flex items-center justify-center px-1"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </div>
    </div>

    <BasicModal
      :open="open"
      position="right"
      class="lg:max-w-[544px]"
      :styles="{ contentPadding: 'p-0' }"
      @close="handleClose"
    >
      <template #topHeader>
        <div class="flex items-center w-full gap-2">
          <div class="text-lg font-medium">Notifications</div>
          <SimplePopover>
            <PulseIconButton variant="ghost" size="large" :icon-size="24" icon="LineEllipsisH" />

            <template #panel="{ close }">
              <div class="flex flex-col text-xsm font-normal text-typography-primary w-[250px] py-2">
                <div
                  class="h-9 flex px-3 gap-3 items-center cursor-pointer hover:bg-neutral-100"
                  @click="
                    () => {
                      handleReadAll();
                      close();
                    }
                  "
                >
                  <PulseLineInbox class="h-5 w-5 fill-current" />
                  <div>Mark all as read</div>
                  <div class="ml-auto font-bold p-2 text-typography-disabled">
                    {{ getOS() === OS.WINDOWS ? 'ALT+U' : 'OPT+U' }}
                  </div>
                </div>
                <div
                  class="h-9 flex px-3 gap-3 items-center cursor-pointer hover:bg-neutral-100"
                  @click="
                    () => {
                      handleDelete({ id: ALL });
                      close();
                    }
                  "
                >
                  <PulseXCircle class="h-5 w-5 fill-current" />
                  <div>Delete all notifications</div>
                </div>

                <hr class="border-neutral-200" />

                <NuxtLink
                  class="h-9 flex px-3 gap-3 items-center cursor-pointer hover:bg-neutral-100"
                  to="/account#app-notifications"
                >
                  <PulseLineSetting class="h-5 w-5 fill-current" />
                  <div>Settings</div>
                </NuxtLink>
              </div>
            </template>
          </SimplePopover>
        </div>
      </template>
      <template #default>
        <div
          ref="scrollRef"
          class="flex flex-col gap-[1px] w-full overflow-y-auto h-[calc(100vh-6rem)]"
          @scroll="handleScroll"
        >
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="flex"
            @mouseover="hoveredNotification = notification"
            @mouseleave="hoveredNotification = null"
          >
            <NuxtLink
              class="flex items-center gap-0 w-full py-1 px-4 font-medium hover:bg-neutral-100 text-xsm"
              :to="NotificationUrlMapper[notification.type](notification.content)"
              @click="handleRead(notification)"
              @contextmenu.prevent="optionListRef[notification.id]?.click()"
            >
              <PulseUserAvatar
                v-if="notification.byUserPicture || notification.byUserName"
                :image="notification.byUserPicture ?? undefined"
                :initials="getInitials(notification.byUserName ?? undefined)"
              />
              <div v-else class="w-10 shrink-0 pl-[1px]">
                <PulseCircleLogo class="h-[30px] w-[30px]" />
              </div>

              <div
                class="flex items-center gap-2 w-full pr-2"
                :class="[!!notification.updatedAt && 'text-typography-disabled font-normal'].filter(Boolean)"
              >
                <NotificationContent :notification="notification" />
              </div>

              <PulseBadge
                variant="neutral"
                size="small"
                class="flex items-center gap-1 text-[11px] text-typography-secondary mr-4"
              >
                <component :is="CategoryIconMap[notification.category]" class="w-4 h-4 fill-current" />
                <div class="capitalize">{{ notification.category.toLowerCase() }}</div>
              </PulseBadge>

              <div class="relative">
                <div class="text-xs font-extralight text-neutral-400" :title="getNotificationTime(notification)">
                  {{ relativeTimeShort(notification.createdAt) }}
                </div>

                <div v-if="hoveredNotification === notification" class="absolute -top-1.5 -right-2">
                  <PulseIconButton
                    variant="ghost"
                    size="medium"
                    :icon-size="24"
                    icon="LineEllipsisH"
                    @click.prevent.stop="optionListRef[notification.id]?.click()"
                  />
                </div>
              </div>
            </NuxtLink>

            <SimplePopover class="translate-x-[-120%] translate-y-[1.25rem]">
              <div :ref="el => (optionListRef[notification.id] = el as HTMLElement)"></div>

              <template #panel="{ close }">
                <div class="flex flex-col text-xsm font-normal text-typography-primary w-[250px] py-2">
                  <div
                    class="h-9 flex px-3 gap-3 items-center cursor-pointer hover:bg-neutral-100"
                    @click="
                      () => {
                        handleRead(notification, false);
                        close();
                      }
                    "
                  >
                    <PulseLineInbox class="h-5 w-5 fill-current" />
                    <div>Mark as read</div>
                    <div class="ml-auto font-bold p-2 text-typography-disabled">U</div>
                  </div>
                  <div
                    class="h-9 flex px-3 gap-3 items-center cursor-pointer hover:bg-neutral-100"
                    @click="handleDelete(notification)"
                  >
                    <PulseXCircle class="h-5 w-5 fill-current" />
                    <div>Delete notification</div>
                    <PulseLineCancel class="ml-auto h-5 w-5 fill-current text-typography-disabled" />
                  </div>
                  <hr class="border-neutral-200" />
                  <NuxtLink
                    target="_blank"
                    class="h-9 flex px-3 gap-3 items-center cursor-pointer hover:bg-neutral-100"
                    :to="NotificationUrlMapper[notification.type](notification.content)"
                    @click="handleRead(notification)"
                  >
                    <PulseLineBookMedical class="h-5 w-5 fill-current" />
                    <div>Open in new tab</div>
                  </NuxtLink>
                </div>
              </template>
            </SimplePopover>
          </div>

          <div v-if="notifications.length === 0">
            <div class="flex h-full p-4">
              <div class="text-xsm font-normal text-typography-disabled">No notifications...</div>
            </div>
          </div>

          <div v-if="loadingMoreNotifications" class="flex justify-center items-center p-4 text-typography-disabled">
            <PulseLoadingSpinner class="w-5 h-5" />
          </div>
        </div>
      </template>
    </BasicModal>
  </div>
</template>
<script setup lang="ts">
import type { Dispatch, StringWithAutocomplete } from '@monolog/shared/types';
import { storeToRefs } from 'pinia';
import { useNotificationStore } from '~/store/notification';
import { relativeTimeShort } from '@shared/utils/date-fns';
import dayjs from 'dayjs';
import { tap } from 'rxjs';
import { NotificationUrlMapper, OS, getOS } from '@helpers';
import * as icons from '@pulse/components/icons';
import { ALL, FEATURE_FLAGS } from '@constants';
import { useTemplateRef } from 'vue';

const notificationStore = useNotificationStore();
const { notifications, unreadCount, lastId, loadingMoreNotifications } = storeToRefs(notificationStore);
const { getFlag } = useLaunchDarkly();

const open = ref(false);
const optionListRef = ref<Record<string, HTMLElement>>({});
const scrollRef = useTemplateRef<HTMLElement>('scrollRef');
const hoveredNotification = ref<Dispatch.Notification | null>(null);

const CategoryIconMap: Record<Dispatch.NotificationCategory, any> = {
  CLAIM: icons.LineClipboardAlt,
  MENTION: icons.LineAt,
  MESSAGE: icons.LineCommentDots,
  NOTE: icons.LineEdit,
  REVIEW: icons.LineStar,
  SETTINGS: icons.LineGlobe,
};

const isNotificationsEnabled = computed(() => getFlag(FEATURE_FLAGS.NOTIFICATIONS, false));

const getInitials = (name?: string) => {
  return name
    ?.split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('');
};

const getNotificationTime = (notification: Dispatch.Notification) => {
  const date = new Date(notification.createdAt);
  const format = date.getFullYear() === new Date().getFullYear() ? 'MMM D h:mm A' : 'MMM D, YYYY h:mm A';

  return dayjs(notification.createdAt).format(format);
};

const handleClose = () => {
  open.value = false;
};

const handleDelete = (notification: { id: StringWithAutocomplete<typeof ALL> }) => {
  notificationStore
    .deleteNotification(notification.id)
    .pipe(tap(() => delete optionListRef.value[notification.id]))
    .subscribe();
};

const handleKeyDown = (e: KeyboardEvent) => {
  const shouldReadAllMacos = e.altKey && e.key === 'Dead';
  const shouldReadAllWindows = e.altKey && e.key === 'u';
  const shouldReadNotification = e.key === 'u';
  const shouldDeleteNotification = e.key === 'Backspace' || e.key === 'Delete';

  if (shouldReadAllMacos || shouldReadAllWindows) {
    handleReadAll();
  } else if (shouldReadNotification && hoveredNotification.value) {
    handleRead(hoveredNotification.value, false);
  } else if (shouldDeleteNotification && hoveredNotification.value) {
    handleDelete(hoveredNotification.value!);
  }
};

const handleRead = (notification: Dispatch.Notification, shouldClose: boolean = true) => {
  if (notification.updatedAt) {
    if (shouldClose) handleClose();
  } else {
    notificationStore
      .markAsRead(notification.id)
      .pipe(
        tap(() => {
          if (shouldClose) handleClose();
        }),
      )
      .subscribe();
  }
};

const handleReadAll = () => {
  notificationStore.markAsRead(ALL).subscribe();
};

const handleScroll = () => {
  if (!scrollRef.value) return;

  const heightThreshold = 150;
  if (
    scrollRef.value?.scrollTop + scrollRef.value?.clientHeight >= scrollRef.value?.scrollHeight - heightThreshold &&
    !loadingMoreNotifications.value &&
    notifications.value.length > 0
  ) {
    notificationStore.fetchMoreNotifications()?.subscribe();
  }
};

watch(
  () => open.value,
  () => {
    if (open.value) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      notificationStore.fetchNotifications({ clear: true }).subscribe();
      window.removeEventListener('keydown', handleKeyDown);
    }
  },
);
</script>
